import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { CompanyForm } from 'src/app/classes/form-groups/company-form';
import { CompanyVendorForm } from 'src/app/classes/form-groups/company-vendor-form';
import { InsuranceAgentForm } from 'src/app/classes/form-groups/insurance-agent-form';
import { Address } from 'src/app/classes/models';
import { InsuranceAgent } from 'src/app/classes/models/insurance-agent';
import { CompanyVendorService } from 'src/app/services/company-vendor.service';
import { InsuranceService } from 'src/app/services/insurance.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';

@Component({
  selector: 'app-add-edit-insurance-agent-modal',
  templateUrl: './add-edit-insurance-agent-modal.component.html',
  styleUrls: ['./add-edit-insurance-agent-modal.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AddEditInsuranceAgentModalComponent implements OnInit {
  constructor(
    private ToastService: ToastService,
    private CompanyVendorService: CompanyVendorService,
    private ActiveModal: NgbActiveModal,
    private UtilityService: UtilityService,
    private InsuranceService: InsuranceService,
    private cdr: ChangeDetectorRef
  ) {}

  @Input() VendorId!: number;
  @Input() CompanyId!: number;
  @Input() InsuranceAgentId?: number;

  country!: 'US' | 'CA';
  VendorMailingAddress!: Address;
  CompanyVendorFormGroup = new CompanyVendorForm();
  InsuranceAgentFormGroup = new FormGroup(new InsuranceAgentForm());
  InsuranceAgents: InsuranceAgent[] = [];

  currentInsuranceAgentPolicies: (
    | {
        label: 'General Liability';
        value: 'isGLProvider';
      }
    | {
        label: 'Commercial Auto Liability';
        value: 'isAutoProvider';
      }
    | {
        label: 'Umbrella Liability';
        value: 'isUmbrellaProvider';
      }
    | {
        label: "Worker's Compensation";
        value: 'isWorkersProvider';
      }
    | {
        label: 'Professional Liability';
        value: 'isProfessionalProvider';
      }
  )[] = [];

  close() {
    this.ActiveModal.dismiss();
  }

  // Reset the agent form to a blank state while preserving the VendorId
  // (which is what the form needs to remain valid in the modal context).
  resetForm() {
    this.InsuranceAgentFormGroup.reset();
    this.InsuranceAgentFormGroup.patchValue({ VendorId: this.VendorId });
    this.currentInsuranceAgentPolicies = [];
    this.cdr.markForCheck();
  }

  save() {
    const errors = this.UtilityService.generateValidationErrorsObject(
      this.InsuranceAgentFormGroup
    );

    if (errors) {
      throw this.ToastService.addErrorToast(
        'Error',
        'There is an error below.',
        { returnError: true }
      );
    }

    const InsuranceAgent = this.InsuranceAgentFormGroup.value;
    this.InsuranceService.saveInsuranceAgent(InsuranceAgent).subscribe({
      next: (InsuranceAgent) => {
        this.ToastService.addSuccessToast(
          'Success',
          'Successfully saved insurance agent.'
        );
        this.ActiveModal.close(InsuranceAgent);
      }
    });
  }

  ngOnInit(): void {
    // Subscribe to form value changes to trigger change detection
    this.InsuranceAgentFormGroup.valueChanges.subscribe({
      next: () => {
        this.cdr.markForCheck();
      }
    });

    if (!this.CompanyId) {
      throw this.ToastService.addErrorToast('Error', 'Missing Client Id.', {
        returnError: true
      });
    }

    if (!this.VendorId) {
      throw this.ToastService.addErrorToast('Error', 'Missing Vendor Id.', {
        returnError: true
      });
    }

    const CompanyVendorQuery = {
      attributes: JSON.stringify([
        'id',
        'CompanyId',
        'VendorId',
        'CAWCNumber',
        'CAWCcertNumber'
      ]),
      CompanyId: this.CompanyId,
      VendorId: this.VendorId,
      Company: JSON.stringify({
        options: { required: true },
        attributes: ['id', 'legalName']
      })
    };

    const VendorQuery = {
      options: { required: true },
      attributes: ['id', 'country'],
      MailingAddress: {
        attributes: ['id', 'VendorId', 'state']
      }
    };

    if (this.InsuranceAgentId) {
      Object.assign(VendorQuery, {
        InsuranceAgent: {
          options: { required: false },
          id: this.InsuranceAgentId
        }
      });
    }

    Object.assign(CompanyVendorQuery, {
      Vendor: JSON.stringify(VendorQuery)
    });

    this.InsuranceAgentFormGroup.patchValue({ VendorId: this.VendorId });

    this.CompanyVendorService.getCompanyVendor(CompanyVendorQuery).subscribe({
      next: (CompanyVendor) => {
        if (!CompanyVendor) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing Client/Vendor information.',
            { returnError: true }
          );
        }

        const { Company, Vendor } = CompanyVendor;

        if (!Company) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing Client information.',
            { returnError: true }
          );
        }

        this.CompanyVendorFormGroup.setControl('Company', new CompanyForm());
        this.CompanyVendorFormGroup.patchValue(CompanyVendor);

        if (!Vendor) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing Vendor information.',
            { returnError: true }
          );
        }

        const { country, MailingAddress, InsuranceAgents } = Vendor;

        if (!country) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing country information.',
            { returnError: true }
          );
        }

        if (!MailingAddress) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Vendor is missing mailing address.',
            { returnError: true }
          );
        }

        if (Array.isArray(InsuranceAgents)) {
          if (InsuranceAgents.length > 0 && InsuranceAgents.length !== 1) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Insurance Agents was not loaded correctly.',
              { returnError: true }
            );
          }

          this.InsuranceAgentFormGroup.patchValue(InsuranceAgents[0]);
          this.InsuranceAgents.push(...InsuranceAgents);
        }

        Object.assign(this, { country, VendorMailingAddress: MailingAddress });
        this.cdr.markForCheck();
      }
    });
  }
}
